import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { LegalModal } from "./LegalModal";
import { CONTACT, whatsappUrl, mailtoUrl } from "../../data";
import { LEGAL, COMPANY } from "../../legal";
import { fadeUp, viewportOnce } from "../../lib/motion";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.5 3h-2.9v11.34a2.42 2.42 0 1 1-2.42-2.42c.14 0 .27.02.4.05V9.02a5.35 5.35 0 0 0-.4-.02 5.34 5.34 0 1 0 5.34 5.34V8.9a7.16 7.16 0 0 0 4.18 1.34V7.34a4.28 4.28 0 0 1-3.1-1.52z" />
  </svg>
);

export const Footer = () => {
  const [modal, setModal] = useState(null);

  return (
    <footer data-testid="site-footer" className="relative bg-ink-2 border-t border-white/10 pt-20 sm:pt-32 pb-12">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="lg:col-span-6">
            <Logo imgClass="h-16 sm:h-24" className="mb-8" />
            <p className="font-body font-light text-sm text-[var(--muted)] max-w-sm leading-relaxed">
              Viajes y experiencias que te eligen a ti. Con base en {CONTACT.base}.
              Come · Viaja · Ama.
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-6">Contacto</div>
            <div className="flex flex-col gap-3 font-body text-sm">
              <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="link-underline w-fit text-[var(--paper)]/80 hover:text-[var(--paper)]">
                WhatsApp · {CONTACT.whatsappDisplay}
              </a>
              <a href={mailtoUrl()} data-testid="footer-email" className="link-underline w-fit text-[var(--paper)]/80 hover:text-[var(--paper)]">
                {CONTACT.email}
              </a>
              <span className="text-[var(--muted)]">{CONTACT.base}</span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-6">Síguenos</div>
            <div className="flex flex-col gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="group flex items-center gap-3 font-body text-sm text-[var(--paper)]/80 hover:text-glacier transition-colors">
                <Instagram className="h-5 w-5" strokeWidth={1.4} />
                {CONTACT.instagramHandle}
                <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a href={CONTACT.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" className="group flex items-center gap-3 font-body text-sm text-[var(--paper)]/80 hover:text-glacier transition-colors">
                <TikTokIcon className="h-5 w-5" />
                TikTok {CONTACT.tiktokHandle}
                <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
        </motion.div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 font-body text-xs text-[var(--muted)]">
          <span>© {new Date().getFullYear()} The W Project. Todos los derechos reservados.</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <button data-testid="footer-legal" onClick={() => setModal("aviso")} className="link-underline hover:text-[var(--paper)]">Aviso legal</button>
            <button data-testid="footer-privacy" onClick={() => setModal("privacidad")} className="link-underline hover:text-[var(--paper)]">Privacidad</button>
            <button data-testid="footer-cookies" onClick={() => setModal("cookies")} className="link-underline hover:text-[var(--paper)]">Cookies</button>
            <button data-testid="footer-cancelacion" onClick={() => setModal("cancelacion")} className="link-underline hover:text-[var(--paper)]">Cancelación</button>
          </div>
        </div>
        <p className="mt-6 font-body text-[0.7rem] text-[var(--muted)]/70">
          {COMPANY.name} · CIF {COMPANY.cif} · {COMPANY.address}
        </p>
      </div>

      {Object.entries(LEGAL).map(([key, doc]) => (
        <LegalModal key={key} open={modal === key} onClose={() => setModal(null)} title={doc.title}>
          {doc.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </LegalModal>
      ))}
    </footer>
  );
};
